import { createContext, useCallback, useEffect, useMemo, useRef } from "react";
import { subDays } from "date-fns";
import { useLocation } from "react-router-dom";
// firestore
import { query, where, limit, addDoc, orderBy, getDocs, Timestamp, collection, startAfter } from "firebase/firestore";
import { db } from "../../services/firebase";
// products
import actionTypes from "../../common/actionTypes";
import { activityLogsReducer } from "../../reducers";
// context
import useAuthContext from "../../hooks/useAuthContext";
import useShopContext from "../../hooks/useShopContext";
import useCustomReducer from "../../hooks/useCustomReducer";
// constants
import { APP_ROUTES, DATABASE_STATUTES } from "../../common/constants";
import { LOG_ACTIONS } from "./actionTypes";

// constants
const SHOPS = "Shops";
const ACTIVITY_LOGS = "ActivityLogs";
const LOG_CHUNK_LIMIT = 25;
const THIRTY_DAYS_AGO = subDays(new Date(), 30);

const initialState = {
  data: [],
  status: DATABASE_STATUTES.NOT_LOADED,
  error: null,
};

const ActivityLogsContext = createContext({
  ...initialState,
});

const ActivityLogsProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(activityLogsReducer, initialState);

  const collectionRef = useRef(null);
  const lastVisible = useRef(null);

  const { pathname } = useLocation();

  const { user } = useAuthContext();
  const { shop } = useShopContext();
  const { uid } = shop;

  if (user && uid) {
    collectionRef.current = collection(db, SHOPS, user.domain, ACTIVITY_LOGS);
  }

  const getQuery = useCallback(() => {
    const QUERY_DATE = Timestamp.fromDate(THIRTY_DAYS_AGO);

    return lastVisible.current
      ? query(
          collectionRef.current,
          where("createdAt", ">", QUERY_DATE),
          limit(LOG_CHUNK_LIMIT + 1),
          orderBy("createdAt", "desc"),
          startAfter(lastVisible.current)
        )
      : query(
          collectionRef.current,
          where("createdAt", ">", QUERY_DATE),
          limit(LOG_CHUNK_LIMIT + 1),
          orderBy("createdAt", "desc")
        );
  }, []);

  const fetchLogs = useCallback(
    async ({ nextPage }) => {
      dispatch({
        type: actionTypes.FETCH_ACTIVITY_LOGS,
        payload: { nextPage },
      });

      try {
        const q = getQuery();

        const documentSnapshots = await getDocs(q);
        const docs = documentSnapshots.docs;

        const hasMore = docs.length > LOG_CHUNK_LIMIT;

        if (hasMore) {
          docs.pop();
        }

        const data = docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));

        lastVisible.current = docs.at(-1);

        dispatch({
          type: actionTypes.FETCH_ACTIVITY_LOGS_SUCCESS,
          payload: data,
        });

        return Promise.resolve(hasMore);
      } catch (error) {
        dispatch({
          type: actionTypes.FETCH_ACTIVITY_LOGS_FAILURE,
          payload: error,
        });

        return Promise.reject(error);
      }
    },
    [dispatch, getQuery]
  );

  const fetchMoreLogs = useCallback(() => fetchLogs({ nextPage: true }), [fetchLogs]);
  const fetchingIsActive = pathname === APP_ROUTES.ACTIVITY_LOG || pathname === APP_ROUTES.DASHBOARD;

  useEffect(() => {
    if (user && uid && fetchingIsActive) {
      fetchLogs({});
    }

    return () => {
      lastVisible.current = null;
    };
  }, [user, uid, fetchLogs, fetchingIsActive]);

  const write = useCallback(
    async (actionType, messageParams) => {
      try {
        dispatch({
          type: actionTypes.WRITE_ACTIVITY_LOGS,
        });

        const action = LOG_ACTIONS[actionType](messageParams);

        await addDoc(collectionRef.current, {
          ...action,
          createdAt: Timestamp.now(),
        });

        dispatch({
          type: actionTypes.WRITE_ACTIVITY_LOGS_SUCCESS,
          action,
        });
      } catch (error) {
        dispatch({
          type: actionTypes.WRITE_ACTIVITY_LOGS_FAILURE,
          payload: error,
        });
      }
    },
    [dispatch]
  );

  const memoizedState = useMemo(
    () => ({
      ...state,
      fetchMoreLogs,
      writeLog: write,
    }),
    [fetchMoreLogs, state, write]
  );

  return <ActivityLogsContext.Provider value={memoizedState}>{children}</ActivityLogsContext.Provider>;
};

export { ActivityLogsContext, ActivityLogsProvider };
